import React, { useEffect, useState } from "react";
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Link, Typography
} from "@mui/material";

/**
 * MicroSlide history panel. Mirrors MicroTip/History.js.
 *
 * Props:
 *   resultDataMap: { [runNumber]: { run, description, result, rows, parameters } }
 *   resultFields:  field names to display in the Result panel
 */
export default function History({ resultDataMap, resultFields = [] }) {
    const [selectedRun, setSelectedRun] = useState(null);
    const historyData = Object.values(resultDataMap || {});

    useEffect(() => {
        const runs = Object.keys(resultDataMap || {});
        if (runs.length > 0) {
            setSelectedRun(Number(runs[runs.length - 1]));
        }
    }, [resultDataMap]);

    const selectedEntry = resultDataMap?.[selectedRun];
    const selectedResult = selectedEntry?.result;
    const selectedDescription = selectedEntry?.description;
    const fields = resultFields.length ? resultFields : Object.keys(selectedResult || {});

    // Predict batches put their per-sample rows in result.predictions; render
    // those as a separate detail table.
    const predictions = Array.isArray(selectedResult?.predictions)
        ? selectedResult.predictions
        : null;

    return (
        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
            <Paper sx={{ flex: 1, p: 2, minHeight: 300 }}>
                <Typography variant="h6" gutterBottom>
                    Result {selectedRun ? `( ${selectedDescription})` : ""}
                </Typography>

                {selectedResult ? (
                    <>
                        <Table size="small">
                            <TableBody>
                                {fields.map(field => (
                                    <TableRow key={field}>
                                        <TableCell><strong>{field}</strong></TableCell>
                                        <TableCell>{selectedResult?.[field] ?? "-"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {predictions && predictions.length > 0 && (
                            <TableContainer sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Sample</strong></TableCell>
                                            <TableCell><strong>Resp1</strong></TableCell>
                                            <TableCell><strong>Resp2</strong></TableCell>
                                            <TableCell><strong>Concentration</strong></TableCell>
                                            <TableCell><strong>Cond</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {predictions.map((p, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{p.sampleId || i + 1}</TableCell>
                                                <TableCell>{p.response1 ?? "-"}</TableCell>
                                                <TableCell>{p.response2 ?? "-"}</TableCell>
                                                <TableCell>{p.concentration ?? "-"}</TableCell>
                                                <TableCell>{p.conditionCode ?? ""}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                ) : (
                    <Typography>No Result Data</Typography>
                )}
            </Paper>

            <Paper sx={{ flex: 1, p: 2, minHeight: 300 }}>
                <Typography variant="h6" gutterBottom>History</Typography>

                {historyData.length > 0 ? (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Run</strong></TableCell>
                                    <TableCell><strong>Description</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyData.map(entry => (
                                    <TableRow
                                        key={entry.run}
                                        hover
                                        selected={selectedRun === entry.run}
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => setSelectedRun(entry.run)}
                                    >
                                        <TableCell>
                                            <Link component="button" underline="hover">
                                                {entry.run}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{entry.description}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>No History</Typography>
                )}
            </Paper>
        </Box>
    );
}
